/**
 * Resume / Rehydrate Engine — pure broadcasters + long-idle session revalidation.
 *
 * Responsibility:
 *   - Listen to native iOS / Android app-state changes via @capacitor/app.
 *   - Fallback to document.visibilitychange on web.
 *   - On foreground after a long idle (≥ REVALIDATE_THRESHOLD_MS), proactively
 *     refresh the access token BEFORE bumping `resumeEpoch`. This eliminates
 *     the "8-fetches-storm-the-401-interceptor" race when subscribers all
 *     refetch concurrently with an expired access token.
 *   - For short trips (Notification Center peek, Control Center pull-down)
 *     skip revalidation — keep the fast path unchanged.
 *   - Increment `resumeEpoch` once revalidation completes (or fails in a way
 *     that subscribers' own 401 interceptors can recover from).
 *   - Expose a separate `tapEpoch` counter that anyone can bump (via
 *     `useBumpTapEpoch()`) when a push-notification tap lands the user
 *     on a route that may already be mounted.
 *
 * Non-responsibilities (intentional):
 *   - Does NOT directly mutate user/auth React state.
 *   - Does NOT trigger page refetches directly — subscribers consume the
 *     epoch counters and own their refetch logic.
 *
 * Session-revalidation injection:
 *   To avoid a circular import with App.js (which already imports this
 *   context), App.js calls `setSessionRevalidator(attemptTokenRefresh)`
 *   and `setSessionInvalidator(logout)` inside a useEffect. If the
 *   setters have not been called yet (early init), revalidation is a
 *   no-op and we fall through to bumping resumeEpoch — same behaviour
 *   as before this engine landed.
 *
 * See architecture audits: Resume Engine (2026-02-28), Tap Engine
 * (2026-02-29), Long-Idle Session Revalidation (2026-02-29).
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import {
  recordBackgrounded as _diagRecordBackgrounded,
  recordResume as _diagRecordResume,
  recordRevalidation as _diagRecordRevalidation,
  recordTapEpoch as _diagRecordTapEpoch,
  recordRawForegroundEvent as _diagRecordRawForegroundEvent,
} from '../utils/diagnostics';

// Suppress rapid foreground/background bounces (e.g. Notification Center
// drag-down, brief Control Center peek). Only one rehydrate per window.
const RESUME_DEBOUNCE_MS = 5000;

// Trips shorter than this skip the token-revalidation step entirely —
// kept aligned with the debounce window so the fast path matches today's
// behaviour. Long-idle (>= this value) triggers the session check.
const REVALIDATE_THRESHOLD_MS = 60 * 1000; // 60 seconds

// When we decode the JWT exp claim, treat any token expiring within this
// window as "needs refresh". Mirrors the buffer used by checkAuth() in
// App.js so the two engines never disagree about token freshness.
const TOKEN_EXP_BUFFER_MS = 60 * 1000; // 60 seconds

// Hard cap on the resume-time refresh attempt. Slightly above the
// attemptTokenRefresh() axios timeout (12s in App.js) so we don't race it.
const REVALIDATE_TIMEOUT_MS = 14000;

// Setter-injected handlers. Populated by App.js after AuthProvider mounts.
// Module-scoped (not React state) because they outlive any single render
// and must be available to the imperative appStateChange handler below.
let _sessionRevalidator = null;
let _sessionInvalidator = null;

/**
 * Register the token-refresh function (App.js → attemptTokenRefresh).
 * Idempotent. Called once during App bootstrap.
 */
export const setSessionRevalidator = (fn) => {
  _sessionRevalidator = typeof fn === 'function' ? fn : null;
};

/**
 * Register the logout/invalidation function (App.js → logout).
 * Called only when revalidation returns a genuine auth failure
 * (HTTP 401/403 from /auth/refresh — NOT a network/timeout error).
 */
export const setSessionInvalidator = (fn) => {
  _sessionInvalidator = typeof fn === 'function' ? fn : null;
};

const ResumeContext = createContext({
  resumeEpoch: 0,
  tapEpoch: 0,
  bumpTapEpoch: () => {},
});

/**
 * Cheap JWT exp inspection. Returns true if the token is expired or will
 * expire within TOKEN_EXP_BUFFER_MS. Returns true on any decode failure
 * (safest assumption — force a refresh attempt).
 */
const isTokenNearExpiry = (token) => {
  if (!token || typeof token !== 'string') return true;
  const parts = token.split('.');
  if (parts.length !== 3) return true;
  try {
    const payload = JSON.parse(atob(parts[1]));
    if (!payload || typeof payload.exp !== 'number') return true;
    const expiryMs = payload.exp * 1000;
    return Date.now() > (expiryMs - TOKEN_EXP_BUFFER_MS);
  } catch {
    return true;
  }
};

/**
 * Wrap any promise in a hard timeout that rejects with an ECONNABORTED
 * error so caller can distinguish a network stall from a real auth
 * failure. Mirrors withHangGuard() in App.js.
 */
const withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => {
    const err = new Error('RESUME_REVALIDATE_TIMEOUT');
    err.code = 'ECONNABORTED';
    reject(err);
  }, ms)),
]);

export const ResumeProvider = ({ children }) => {
  const [resumeEpoch, setResumeEpoch] = useState(0);
  const [tapEpoch, setTapEpoch] = useState(0);
  const lastResumeAtRef = useRef(0);
  const lastBackgroundedAtRef = useRef(0);
  const wasVisibleRef = useRef(true);
  const revalidationInFlightRef = useRef(false);
  // Track epoch values OUTSIDE React state so diagnostics can record the
  // accurate "next" value synchronously before setState commits. React 18
  // setState updaters are not guaranteed to run as expected for side
  // effects — moving the diagnostic side effect to a ref-tracked counter
  // fixes the lastResumeAt-stays-null bug observed in TestFlight.
  const _lastResumeEpochSeen = useRef(0);
  const _lastTapEpochSeen = useRef(0);

  // Stable bumper so subscribers can include it in useEffect deps without
  // re-triggering. Records diagnostics SYNCHRONOUSLY (not inside the
  // setState updater) because React 18 updater functions are not a safe
  // place to run side effects — they may be batched, deferred, or in
  // some Concurrent paths not invoked at all for a no-change update.
  const bumpTapEpoch = useCallback(() => {
    const nextEpoch = (_lastTapEpochSeen.current || 0) + 1;
    _lastTapEpochSeen.current = nextEpoch;
    try { _diagRecordTapEpoch(nextEpoch); } catch { /* noop */ }
    setTapEpoch(nextEpoch);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[ResumeEngine] tapEpoch bumped (push-tap)');
    }
  }, []);

  // Internal: actually bump the resume counter (debounced). Records the
  // diagnostic event BEFORE setState so the diagnostics snapshot is
  // always accurate even if React defers the commit. This is the line
  // that was missing on prior branches and caused `lastResumeAt` /
  // `lastResumeEpoch` to never populate despite revalidation succeeding.
  const bumpResumeEpoch = useCallback((reason) => {
    const now = Date.now();
    if (now - lastResumeAtRef.current < RESUME_DEBOUNCE_MS) {
      return;
    }
    lastResumeAtRef.current = now;
    const nextEpoch = (_lastResumeEpochSeen.current || 0) + 1;
    _lastResumeEpochSeen.current = nextEpoch;
    try {
      _diagRecordResume({
        reason,
        idleMs: now - (lastBackgroundedAtRef.current || now),
        resumeEpoch: nextEpoch,
      });
    } catch { /* noop */ }
    setResumeEpoch(nextEpoch);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log(`[ResumeEngine] resumeEpoch bumped (${reason})`);
    }
  }, []);

  // The core engine path: invoked when isActive flips to true.
  // Decides whether to revalidate first or bump immediately.
  const handleForeground = useCallback(async (label) => {
    const now = Date.now();
    const idleMs = now - (lastBackgroundedAtRef.current || now);

    // ── Fast path: short trips skip the auth check ──────────────────
    if (idleMs < REVALIDATE_THRESHOLD_MS) {
      bumpResumeEpoch(`${label}:fast-path:${idleMs}ms`);
      return;
    }

    // ── Long idle: inspect the access token ─────────────────────────
    const token = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
    if (!token) {
      // No token at all — Protected routes will redirect. Just bump.
      bumpResumeEpoch(`${label}:no-token:${idleMs}ms`);
      return;
    }

    if (!isTokenNearExpiry(token)) {
      // Token still fresh — long idle but no refresh needed.
      bumpResumeEpoch(`${label}:token-fresh:${idleMs}ms`);
      return;
    }

    // ── Token expired / near-expiry: revalidate before bumping ──────
    if (!_sessionRevalidator) {
      // Engine wired but App.js hasn't injected the revalidator yet.
      // Fall through to today's behaviour — subscribers' 401 interceptor
      // will recover.
      bumpResumeEpoch(`${label}:no-revalidator:${idleMs}ms`);
      return;
    }

    // Prevent overlapping revalidations if two foreground events arrive
    // within the same async window (Capacitor + visibilitychange both
    // firing). The debounce on bumpResumeEpoch already collapses the
    // visible side effect, but the network call should not be doubled.
    if (revalidationInFlightRef.current) {
      return;
    }
    revalidationInFlightRef.current = true;

    try {
      await withTimeout(Promise.resolve().then(_sessionRevalidator), REVALIDATE_TIMEOUT_MS);
      // Refresh succeeded: localStorage 'token' and 'user' have been
      // updated by attemptTokenRefresh(). Now bump so subscribers refetch
      // with the fresh token.
      try { _diagRecordRevalidation({ outcome: 'refresh-success' }); } catch { /* noop */ }
      bumpResumeEpoch(`${label}:post-revalidate:${idleMs}ms`);
    } catch (err) {
      // Granular classification — distinguish stalled-IPC, refresh timeout,
      // refresh 401, missing refresh token, and generic network failure.
      const code = err?.code || (err?.message?.includes('RESUME_REVALIDATE_TIMEOUT') ? 'RESUME_REVALIDATE_TIMEOUT' : null);
      let outcome;
      let isTerminal = false;
      switch (code) {
        case 'NATIVE_TOKEN_READ_TIMEOUT':
          outcome = 'native-token-read-timeout'; isTerminal = true; break;
        case 'REFRESH_TOKEN_MISSING':
          outcome = 'refresh-token-missing'; isTerminal = true; break;
        case 'REFRESH_REQUEST_TIMEOUT':
        case 'RESUME_REVALIDATE_TIMEOUT':
        case 'ECONNABORTED':
          outcome = 'refresh-request-timeout'; isTerminal = true; break;
        case 'REFRESH_REQUEST_401':
          outcome = 'refresh-request-401'; isTerminal = true; break;
        case 'NETWORK_FAIL':
          outcome = 'network-fail'; isTerminal = false; break;
        default:
          // Fallback: HTTP 401/403 on the response is also auth-terminal.
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            outcome = 'refresh-request-401'; isTerminal = true;
          } else if (!err?.response) {
            outcome = 'network-fail'; isTerminal = false;
          } else {
            outcome = code || 'unknown'; isTerminal = true;
          }
      }
      try { _diagRecordRevalidation({ outcome, errorMessage: `${code || ''} ${err?.message || ''}`.trim() }); } catch { /* noop */ }

      if (!isTerminal) {
        // Transient — bump anyway. Subscribers will fetch with the
        // (still-expired) token; their request interceptor (Global Auth
        // Gate) will retry the refresh once the network recovers.
        bumpResumeEpoch(`${label}:revalidate-${outcome}:${idleMs}ms`);
      } else {
        // Terminal — bump the resume epoch so diagnostics records the
        // resume event (lastResumeAt populates), but ALSO invalidate the
        // session so dashboards do not fire protected API calls with the
        // expired token. The Global Auth Gate's sessionExpiredFlag then
        // short-circuits all subsequent protected requests.
        bumpResumeEpoch(`${label}:revalidate-${outcome}:${idleMs}ms`);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[ResumeEngine] terminal revalidation outcome — invalidating session', outcome, err);
        }
        if (_sessionInvalidator) {
          try { await _sessionInvalidator(); } catch { /* noop */ }
        }
      }
    } finally {
      revalidationInFlightRef.current = false;
    }
  }, [bumpResumeEpoch]);

  useEffect(() => {
    // ── Native lifecycle (Capacitor / iOS / Android) ──────────────────
    let capListener = null;
    try {
      const result = CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          // Belt-and-braces: record the RAW event BEFORE any decision
          // logic so diagnostics reflects whether Capacitor even fired.
          try { _diagRecordRawForegroundEvent('capacitor:appStateChange'); } catch { /* noop */ }
          handleForeground('capacitor:appStateChange').catch(() => { /* swallow */ });
        } else {
          // Mark when we went to background so the next foreground
          // can compute idle duration accurately.
          lastBackgroundedAtRef.current = Date.now();
          try { _diagRecordBackgrounded(); } catch { /* noop */ }
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[ResumeEngine] backgrounded at', new Date().toISOString());
          }
        }
      });
      Promise.resolve(result).then((handle) => { capListener = handle; }).catch(() => { /* noop */ });
    } catch {
      // Capacitor plugin not available (e.g. web preview). No-op.
    }

    // ── Web fallback (visibilitychange) ───────────────────────────────
    const onVisibility = () => {
      const isVisible = !document.hidden;
      if (isVisible && !wasVisibleRef.current) {
        try { _diagRecordRawForegroundEvent('visibilitychange'); } catch { /* noop */ }
        handleForeground('visibilitychange').catch(() => { /* swallow */ });
      } else if (!isVisible && wasVisibleRef.current) {
        lastBackgroundedAtRef.current = Date.now();
        try { _diagRecordBackgrounded(); } catch { /* noop */ }
      }
      wasVisibleRef.current = isVisible;
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      try {
        if (capListener && typeof capListener.remove === 'function') {
          capListener.remove();
        }
      } catch { /* noop */ }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [handleForeground]);

  return (
    <ResumeContext.Provider value={{ resumeEpoch, tapEpoch, bumpTapEpoch }}>
      {children}
    </ResumeContext.Provider>
  );
};

export const useResumeEpoch = () => useContext(ResumeContext).resumeEpoch;
export const useTapEpoch = () => useContext(ResumeContext).tapEpoch;
export const useBumpTapEpoch = () => useContext(ResumeContext).bumpTapEpoch;
